import { Fade } from '@mui/material'
import { GridValidRowModel } from '@mui/x-data-grid-premium'
import { GridCellParams } from '@mui/x-data-grid-pro'
import cn from 'classnames'
import Processing from 'ext-ui/components/Processing/Processing'
import Tooltip from 'ext-ui/components/Tooltip/Tooltip'
import { LoadingState } from 'ext-ui/hooks/useLoadingStateAnimation'
import { text_colorTextPrimary } from 'ext-ui/style/colors'
import React, { useState } from 'react'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgClose from 'src/components/Svgs/SvgClose'
import { useTranslate } from 'src/localization/Trns'

import styles from './DataGridEditableCell.style'

export type DataGridEditableCellType = 'text' | 'select'

export interface DataGridEditableCellProps<Row extends GridValidRowModel> {
  value?: React.ReactNode
  type?: DataGridEditableCellType
  color?: 'highlight' | 'severe'
  bold?: boolean
  className?: string
  tooltipContent?: React.ReactNode
  params: GridCellParams<Row>
  editMode: 'row' | 'cell'
  onClear?: () => void
  loadingState?: LoadingState
}

const DataGridEditableCell = <Row extends GridValidRowModel>(
  props: DataGridEditableCellProps<Row>,
): React.ReactElement => {
  const {
    className,
    value,
    type = 'text',
    color,
    bold = false,
    tooltipContent,
    params,
    editMode,
    onClear,
    loadingState,
  } = props

  const translate = useTranslate()

  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const disabled = params.isEditable === false || loadingState === 'pending'
  const alignRight = params.colDef.align === 'right'
  const displayValueIsEmpty =
    value === undefined || (typeof value === 'string' && value.trim() === '')

  const shouldShowClear =
    type === 'select' &&
    !!params.value &&
    (isHovered || isFocused) &&
    !disabled &&
    !!onClear

  const handleClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) {
      return
    }

    if (editMode === 'row') {
      params.api.startRowEditMode({
        id: params.id,
      })
    } else {
      params.api.startCellEditMode({
        id: params.id,
        field: params.field,
      })
    }
  }

  const handleClearClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation() // Prevent edit mode from triggering
    onClear?.()
  }
  return (
    <DataGridCellWrapper
      className={className}
      css={styles}
      disabled={disabled}
      params={params}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={cn('wrapper', color, { disabled })}
        role={'button'}
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === ' ') {
            handleClick(e)
          }
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        aria-disabled={disabled}
      >
        <Tooltip
          tooltipContent={tooltipContent}
          placement={'bottom-start'}
          enterDelay={750}
        >
          <div className={cn('text-container', { bold, alignRight })}>
            <div className={cn('text', color)}>
              {displayValueIsEmpty ? '--' : value}
            </div>
            {loadingState === 'pending' ? (
              <div className={'icon'}>
                <Processing width={16} height={16} viewBox={'0 0 24 24'} />
              </div>
            ) : type === 'select' ? (
              <div className={'icons-container'}>
                <Fade
                  in={shouldShowClear}
                  timeout={300}
                  unmountOnExit
                  mountOnEnter
                >
                  <button
                    className="icon"
                    aria-label={translate('Clear selection')}
                    onClick={handleClearClick}
                  >
                    <SvgClose
                      stroke={text_colorTextPrimary}
                      width={16}
                      height={16}
                      viewBox={'0 0 24 24'}
                    />
                  </button>
                </Fade>
                <div className={'icon'}>
                  <SvgChevronDown
                    fill={text_colorTextPrimary}
                    width={'100%'}
                    height={'100%'}
                    viewBox={'0 0 24 24'}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </Tooltip>
      </div>
    </DataGridCellWrapper>
  )
}

export default DataGridEditableCell
